import React, { useCallback, useState } from "react";
import { useAppContext } from "../../hooks/useAppContext";
import { useClient } from "../../hooks/useClient";
import { useErrorHandling } from "../../hooks/useErrorHandling";
import { useIsAuthenticated } from "../../hooks/useIsAuthenticated";
import { useLabels } from "../../hooks/useLabels";
import { showLoginDialogAsync } from "../dialogs/login-dialog";
import { PhotoSource } from "../photo-source";
import { usePartnerConnectWindow } from "../use-partner-connect-window";
import { labels } from "./labels";
import { ILocalPhotosImport, PhotoSourceLocal } from "./photo-source-local";
import { usePhotoStores } from "../../hooks/use-photo-stores";
import { BusyBlocker } from "../busy-blocker/busy-blocker";

/**
 * This handles makes sure the you are connected to this partner before calling the onSelect callback
 */
export const PhotoSourcesContainer = ({
	onSelect,
}: {
	/**
	 * Only the selected photoStore with optionally the selected photos
	 * Photo selection iframe is opened in parent
	 */
	onSelect: (
		result: { photoStore: eXpress.core.IPhotoStore | undefined } | ILocalPhotosImport,
		didLogin: boolean,
	) => void;
}) => {
	const { editor, googleAnalytics, platform } = useAppContext();

	const client = useClient();

	const isAuthenticated = useIsAuthenticated();

	const [[connectedAsLabel, myDeviceLabel], areLabelsLoading] = useLabels([
		labels.connectedAs,
		labels.myDevice,
	]);

	const { createErrorHandler } = useErrorHandling();

	const showPartnerconnectWindowAsync = usePartnerConnectWindow();

	const { data: photoStores, isLoading: areStoresLoading } = usePhotoStores();
	const [disablePhotoSourceButtons, setDisablePhotoSourceButtons] = useState(false);

	const handlePhotoSourceClick = useCallback(
		async (photoStore: eXpress.core.IPhotoStore) => {
			if (disablePhotoSourceButtons) {
				return;
			}
			setDisablePhotoSourceButtons(true);

			if (photoStore.storeType === eXpress.core.StoreType.Local) {
				// Don't log
			} else if (photoStore.storeType === eXpress.core.StoreType.eXpress) {
				// Smartphoto
				googleAnalytics.trackEventToGa({
					trackingAction: "PhotoSourceSmartphotoAlbums",
				});
			} else {
				// Remove spaces from name 'Google Photos' -> GooglePhotos
				const gaActionName = `PhotoSource${photoStore.name.replace(/ /g, "")}` as const;
				googleAnalytics.trackEventToGa({ trackingAction: gaActionName });
			}

			if (photoStore.isConnected && photoStore.hasAlbumAccess) {
				// Already connected to the store
				onSelect({ photoStore }, false);
				setDisablePhotoSourceButtons(false);
			} else if (photoStore.storeType === eXpress.core.StoreType.eXpress) {
				// Smartphoto login popup
				try {
					const isLoggedIn = await showLoginDialogAsync(
						eXpress.creator.LoginFlow.Default,
						{
							editor,
						},
					);
					onSelect(
						{
							photoStore: isLoggedIn
								? { ...photoStore, isConnected: true, hasAlbumAccess: true }
								: photoStore,
						},
						!!isLoggedIn,
					);
				} catch (err) {
					createErrorHandler()(err);
				} finally {
					setDisablePhotoSourceButtons(false);
				}
			} else {
				// Partner connect popup
				try {
					const didLogin = await showPartnerconnectWindowAsync(photoStore);
					if (didLogin) {
						const stores = await client.getConnectedPhotoStoresAsync();

						if (stores) {
							// Only continue if store is connected
							let photoStoreToUse: eXpress.core.IPhotoStore | undefined =
								stores.find((store) => store.storeType === photoStore.storeType) ||
								photoStore;
							if (!photoStoreToUse.isConnected || !photoStoreToUse.hasAlbumAccess)
								photoStoreToUse = undefined;

							// check if the store now has 'isConnected' flag
							onSelect(
								{
									photoStore: photoStoreToUse,
								},
								// Detect if user completed the login flow by checking photoStore status
								didLogin &&
									!!photoStoreToUse?.isConnected &&
									!!photoStoreToUse?.hasAlbumAccess,
							);
						}
					}
				} catch (err) {
					createErrorHandler()(err);
				} finally {
					setDisablePhotoSourceButtons(false);
				}
			}
		},
		[
			client,
			editor,
			createErrorHandler,
			onSelect,
			showPartnerconnectWindowAsync,
			googleAnalytics,
			disablePhotoSourceButtons,
		],
	);

	// Show loading overlay when stores are not yet loaded
	const isBusy = areStoresLoading || areLabelsLoading;

	return (
		<>
			{/* Requested to show all stores at once instead of adding partner stores when loaded */}
			{!areLabelsLoading && !areStoresLoading ? (
				<>
					<PhotoSourceLocal
						accept={platform.browserInfo.getUploadAcceptForImages()}
						name={myDeviceLabel}
						onChange={(fileSelection) => {
							onSelect(fileSelection, false);
						}}
					/>

					{isAuthenticated() && photoStores
						? photoStores.map((photoStore) => (
								<PhotoSource
									key={photoStore.id}
									{...photoStore}
									disabled={disablePhotoSourceButtons}
									onClick={() => {
										handlePhotoSourceClick(photoStore).catch(
											createErrorHandler(),
										);
									}}
									partnerLogoSvgUrl={photoStore.partnerLogoSvgUrl?.getAbsoluteUrl(
										client,
									)}
									connectedText={
										photoStore.connectedUserName
											? eXpress.utils.formatString(
													connectedAsLabel,
													photoStore.connectedUserName,
											  )
											: undefined
									}
								/>
						  ))
						: null}
				</>
			) : null}
			<BusyBlocker isOpen={isBusy} />
		</>
	);
};
PhotoSourcesContainer.displayName = "PhotoSourcesContainer";
